import {
  IChartConfig,
  IChartSeries,
  IBoxAndWhiskerDataPoints,
  IScatterDataPoints
} from './highchart-platform-interfaces';

import { Descriptive } from './../../classes/Descriptive';
import { HighChartDataAdapter } from './highchart-platform-data-adapter';
import * as _ from 'lodash';

export class ChartConfigBuilder implements IChartConfig {
  credits: IChartConfig['credits'] = {
    enabled: false
  };
  chart: IChartConfig['chart'] = <IChartConfig['chart']>{};
  title: IChartConfig['title'] = <IChartConfig['title']>{};
  subtitle: IChartConfig['subtitle'] = <IChartConfig['subtitle']>{};
  legend: IChartConfig['legend'] = <IChartConfig['title']>{};
  xAxis: any = {};
  yAxis: any = {};
  plotOptions: any;
  series: IChartSeries[];
  tooltip = {};

  constructor() { }

  getBaseConfig(type, title, xAxis, subtitle?): IChartConfig {
    this.chart.type = type;
    this.chart.zoomType = 'xy';
    this.title.text = title;
    this.subtitle.text = subtitle;
    this.legend.enabled = true;

    if (xAxis === 'datetime' && type === 'scatter') {
      this.handleScatterDatetime(xAxis);
    }
    if (type === 'treemap') {
      this.prepareTreeMapConfig(type, title);
    }
    return this;
  }
  // TODO refactor this function, its not mantainable
  addSeries(data: IChartSeries[]): IChartConfig {
    this.series = data;
    this.plotOptions = {};
    this.plotOptions['series'] = {};
    if (this.chart.type === 'boxplot') {
      _.forEach(data, ((series) => {
        if (series.type !== 'scatter') {
          this.xAxis.categories = data[0].boxplotCategories;
          if (data[0].isGrouped) {
            this.plotOptions.series['colorByPoint'] = true;
          }
        } else {

          // When a series contains a data array that is longer than this, only one dimensional arrays of
          // numbers, or two dimensional arrays with x and y values are allowed. Also, only the first point is
          // tested, and the rest are assumed to be the same format. This saves expensive data checking and
          // indexing in long series. Set it to 0 disable.

          this.plotOptions.series['turboThreshold'] = 0;

          // To disable boosting on the series, set the boostThreshold to 0.
          //  in boost mode chart was not picking up the applied colors.

          this.plotOptions.series['boostThreshold'] = 0;
        }
      }))
    }
    return this;
  }

  private prepareTreeMapConfig(type: any, title: any): any { }

  private handleScatterDatetime(xAxis: any) {
    this.xAxis.type = xAxis;
    this.tooltip['headerFormat'] = '<b>{series.name}</b><br>';
    this.tooltip['pointFormat'] = '{point.x:%e. %b}: {point.y:.2f} m';
  }
}

export class ScatterDataPoint {
  constructor(public x: number, public y: number, public label?: string) {
    this.x = x;
    this.y = y;
    this.label = label;
  }
}

export class ChartStatsCalc {

  private _data: any[];

  constructor(public data: any[]) {
    this._data = Array.prototype.slice.call(data).sort((a, b) => Number(a) - Number(b));
  }

  getPercentile(percentile: number): number {
    if (this._data.length === 0) {
      return 0;
    }
    if (typeof percentile !== 'number') {
      throw new TypeError('p must be a number');
    }
    if (percentile <= 0) {
      return this._data[0];
    }
    if (percentile >= 1) {
      return this._data[this._data.length - 1]
    }

    let index = this._data.length * percentile,
      lower = Math.floor(index),
      upper = lower + 1,
      weight = index % 1;

    if (upper >= this._data.length) {
      return this._data[lower];
    }
    return this._data[lower] * (1 - weight) + this._data[upper] * weight;
  }

  getThreeSigmaHigh(): number {
    return Descriptive.mean(this._data) + (Descriptive.stdDev(this._data) * 3);
  }

  getThreeSigmaLow(): number {
    return Descriptive.mean(this._data) + (Descriptive.stdDev(this._data) * -3);
  }

  getWhiskerHigh(): number {
    let IQR = this.getPercentile(.75) - this.getPercentile(.25);
    return Math.min(Descriptive.median(this._data) + (1.5 * IQR), Descriptive.max(this._data));
  }

  getWhiskerLow(): number {
    let IQR = this.getPercentile(.75) - this.getPercentile(.25)
    return Math.max(Descriptive.median(this._data) - (1.5 * IQR), Descriptive.min(this._data));
  }
  getMedian(): number {
    return Descriptive.median(this._data);
  }

  getLowerQuantile(): number {
    let lowerDataSet = this._data.slice(0, this.getMiddleIndex());
    return Descriptive.median(lowerDataSet);
  }

  getUpperQuantile(): number {
    let upperDataSet = this._data.slice(Number(this.getMiddleIndex()) + 1, this._data.length);
    return Descriptive.median(upperDataSet);
  }

  private getMiddleIndex(): number {
    const middleIndex = this._data.length / 2;

    if (this._data.length % 2 === 0) {
      return middleIndex;
    } else {
      return Math.floor(middleIndex);
    }

  }
}

